import React, { useState, useMemo, useEffect } from 'react';
import Box from '@mui/material/Box';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import dayjs from 'dayjs';
import { BrandLoveData } from '../hooks/useInfluenceIndexData';
import { distinctColor } from '../utils/colors';
import { SectionCard, SectionHeader, NestedPanel } from './ui/Section';
import { RankBarChart } from './ui/charts';

interface RankData {
  name: string;
  value: number;
}

interface InfluenceRankingProps {
  data: BrandLoveData | null;
  selectedBrands: string[]; // For personal_branding, this contains selected People
  startDate: string | null;
  endDate: string | null;
}

function InfluenceRanking({ data, selectedBrands = [], startDate, endDate }: InfluenceRankingProps) {
  // All hooks must be called at the top level
  const [view, setView] = useState('average_influence');
  
  // Add debug logging when data changes
  useEffect(() => {
    if (data) {
      console.log('InfluenceRanking data:', data);
    }
  }, [data]);

  // Extract and memoize chart data (safely handling null data)
  const chartData = useMemo(() => {
    if (!data) return { brands: [], dates: [], values: [], postCounts: [], yearlyMetrics: {}, dateRangeMetrics: {} };
    return {
      brands: data.blove_chart.brands,
      dates: data.blove_chart.dates,
      values: data.blove_chart.values,
      postCounts: data.post_count_chart?.values || [],
      yearlyMetrics: data.yearly_metrics || {},
      dateRangeMetrics: data.date_range_metrics || {}
    };
  }, [data]);

  // Function to create rank data for YTD (using backend metrics)
  const createYearlyRankData = useMemo(() => {
    if (!data) return [] as RankData[];
    const { brands, yearlyMetrics } = chartData;
    
    return brands
      .filter((brand) => 
        selectedBrands.length === 0 || selectedBrands.includes(brand)
      )
      .map((brand) => {
        const metrics = yearlyMetrics[brand] || { avg_blove: 0, avg_engagement: 0, post_volume: 0 };
        let metricValue = 0;
        
        switch (view) {
          case 'average_influence':
            metricValue = metrics.avg_blove;
            break;
          case 'average_engagement':
            metricValue = metrics.avg_engagement;
            break;
          case 'post_volume':
            metricValue = metrics.post_volume;
            break;
          default:
            metricValue = metrics.avg_blove;
            break;
        }

        return {
          name: brand,
          value: metricValue
        };
      })
      .sort((a, b) => b.value - a.value);
  }, [chartData, selectedBrands, view, data]);

  // Function to create rank data for date range (using backend metrics)
  const createDateRangeRankData = useMemo(() => {
    if (!data) return [] as RankData[];
    const { brands, dateRangeMetrics } = chartData;
    
    return brands
      .filter((brand) => 
        selectedBrands.length === 0 || selectedBrands.includes(brand)
      )
      .map((brand) => {
        const metrics = dateRangeMetrics[brand] || { avg_blove: 0, avg_engagement: 0, post_volume: 0 };
        let metricValue = 0;
        
        switch (view) {
          case 'average_influence':
            metricValue = metrics.avg_blove;
            break;
          case 'average_engagement':
            metricValue = metrics.avg_engagement;
            break;
          case 'post_volume':
            metricValue = metrics.post_volume;
            break;
          default:
            metricValue = metrics.avg_blove;
            break;
        }

        return {
          name: brand,
          value: metricValue
        };
      })
      .sort((a, b) => b.value - a.value);
  }, [chartData, selectedBrands, view, data]);

  // Handle toggles
  const handleViewChange = (
    event: React.MouseEvent<HTMLElement>,
    newView: string
  ) => {
    if (newView !== null) {
      setView(newView);
    }
  };

  // Early return if data is null
  if (!data) {
    return null;
  }

  // Function to format values based on the current view
  const formatValue = (value: number) => {
    if (view === 'post_volume') {
      return Math.round(value).toLocaleString(); // Format as integer
    } else {
      return Math.round(value).toLocaleString(); // Format as integer for influence and engagement
    }
  };

  // Create appropriate labels based on the view
  const getValueLabel = () => {
    switch(view) {
      case 'average_engagement':
        return 'Avg. Engagement';
      case 'post_volume':
        return 'Post Count';
      default:
        return 'Influence Score';
    }
  };

  const valueLabel = getValueLabel();

  // Same per-person colors as the Influence Index chart above
  const allPeople = data?.blove_chart?.brands ?? [];
  const personColor = (name: string) => {
    const i = allPeople.indexOf(name);
    return i >= 0 ? distinctColor(i, allPeople.length) : undefined;
  };

  const renderRanking = (title: string, rows: RankData[]) => (
    <NestedPanel sx={{ flex: '1 1 420px' }}>
      <SectionHeader title={title} level="sub" />
      <RankBarChart
        data={rows.map((r) => ({ ...r, color: personColor(r.name) }))}
        formatValue={formatValue}
        valueLabel={valueLabel}
        categoryWidth={150}
        maxLabelLength={20}
      />
    </NestedPanel>
  );

  return (
    <SectionCard>
      <SectionHeader
        title="Influence Ranking"
        info="Ranks each person by Average Influence (daily influence score), Average Engagement per post, or Post Volume. The first chart covers the selected dates, the second is year to date."
        actions={
          <ToggleButtonGroup
            value={view}
            exclusive
            onChange={handleViewChange}
            aria-label="influence view"
            size="small"
          >
            <ToggleButton value="average_influence">Average Influence</ToggleButton>
            <ToggleButton value="average_engagement">Average Engagement</ToggleButton>
            <ToggleButton value="post_volume">Post Volume</ToggleButton>
          </ToggleButtonGroup>
        }
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
        {renderRanking('Selected date range', createDateRangeRankData)}
        {renderRanking(`${dayjs().year()} year to date`, createYearlyRankData)}
      </Box>
    </SectionCard>
  );
}

export default InfluenceRanking; 